import { LinearGradient } from 'expo-linear-gradient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { FlatList, Pressable, useColorScheme } from 'react-native';
import { useTheme, View, XStack, YStack } from 'tamagui';
// Lucide replaces the hand-rolled ProfileIcon/ThreadIcon components and
// the inline ChevronRightIcon copy (identical lucide marks). The inline
// copy kept its old 2.5 stroke via strokeWidth at its call site.
import { ChevronRight as ChevronRightIcon, Clock, Users as UsersIcon, User as ProfileIcon } from 'lucide-react-native';

import { Text } from '@/components/text';
import { AttachmentList } from '@/components/attachment-list';
import { Linkified } from '@/components/linkified';
import { Badge } from '@/components/badge';
import { CATEGORY_ICONS, CategoryChip } from '@/components/category-chip';
import { Metadata } from '@/components/metadata';
import { RiseIn } from '@/components/rise-in';
import type { ComponentProps } from 'react';
import { Card } from '@/components/card';
import { VoteControl } from '@/components/vote-control';
import { EmptyState } from '@/components/empty-state';
import { SolidSurface } from '@/components/glass-surface';
import { ListExplainer } from '@/components/list-explainer';
import { PrimaryButton } from '@/components/primary-button';
import { PseudonymousAvatar } from '@/components/pseudonymous-avatar';
import { hexToRgba, ScreenContainer } from '@/components/screen-container';
import { useDockInset, useDockScroll } from '@/lib/dock';
import { ResourceListView } from '@/components/resource-list-view';
import { Skeleton } from '@/components/skeleton';
import { WebRefreshButton } from '@/components/web-refresh';
import { useAuth } from '@/hooks/auth-context';
import { useActivity } from '@/hooks/use-activity';
import { useMyProfile } from '@/hooks/use-my-profile';
import { usePaginatedResource } from '@/hooks/use-paginated-resource';
import { useVote } from '@/hooks/use-vote';
import { api, ApiError, type ThreadCategory, type ThreadListItem } from '@/lib/api';
import { displayHandle } from '@/lib/avatar-identity';
import { distanceMeters, formatDistance, radiusFrameLabel, type Coordinates } from '@/lib/distance';
import { queryKeys } from '@/lib/query-keys';
import { relativeTime } from '@/lib/relative-time';
import { isSparseList } from '@/lib/list-state';
import { isBusyThread } from '@/lib/thread-activity';
import { categoryLabel, THREAD_CATEGORIES } from '@/lib/thread-category';
import { iconSize, iconStroke } from '@/theme/icon';
import { borderWidth, darkColors, lightColors, space } from '@/theme/tokens.generated';
import { useIsDesktop } from '@/lib/use-desktop';
import { RingGlyph } from '@/components/ring-glyph';
import { hit } from '@/lib/hit-slop';

// Card component (src/components/card.tsx) carries the shared
// warm/soft material recipe -- see that file's comment for what it
// replaced and why.

// relativeTime lives in lib/relative-time.ts, not here -- it's this
// list's one differentiator (per this project's own composition rule:
// "a long list needs one differentiator... surface something that
// varies so the eye has somewhere to land"), and thread-detail's post
// list reuses the exact same treatment for the same reason.

// Real screen, not a stub -- issue #15's list slice.
export function Threads() {
  const dockInset = useDockInset();
  const dockScroll = useDockScroll();
  const isDesktop = useIsDesktop();
  const router = useRouter();
  const auth = useAuth();
  const currentUserId = auth.status === 'signed-up' ? auth.user.id : undefined;
  // Native semantic color, not a Tamagui token -- RefreshControl is a
  // real native view (UIRefreshControl/Android SwipeRefreshLayout) that
  // takes a resolved color string, the same reasoning as
  // (app)/_layout.tsx's tabBarActiveTintColor a few files up.
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;
  // undefined = "All" (issue #266's own default). Included in the
  // queryKey so switching categories fetches a fresh first page rather
  // than reusing another category's cached pages.
  const [category, setCategory] = useState<ThreadCategory | undefined>(undefined);
  const threadsQuery = useInfiniteQuery({
    queryKey: queryKeys.threads(category),
    queryFn: ({ pageParam }) => api.listThreads(pageParam, category),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.nextCursor ? lastPage.nextCursor : undefined),
    // A 400 here means "no profile yet" (see api.ts's listThreads doc
    // comment) -- a stable, deterministic response until the user
    // creates one, not a transient failure worth TanStack's default
    // 3-attempt exponential backoff (which would leave the "create a
    // profile" CTA below not appearing for several seconds). Matches
    // the Preview screen's previewThreads precedent.
    retry: false,
  });
  // The list machine (states, refresh haptic, onEndReached double-fire
  // guard) now lives in usePaginatedResource / ResourceListView; the
  // screen keeps only its own query policy and its card.
  const threads = usePaginatedResource(threadsQuery, (pages) => pages?.flatMap((page) => page.threads) ?? []);

  // Own coordinates for the distance line on each ThreadCard below --
  // Thread.location is deliberately public/exact (unlike the jittered
  // Profile.location shown for *other* users, see design.md's
  // Geo-privacy section), so a plain client-side haversine against the
  // viewer's own profile needs no new privacy-sensitive backend work.
  // `retry: false` mirrors the threads query above: a 404 here (no
  // Profile yet) is a stable outcome, not a transient failure worth
  // retrying. This query's own loading/error state never blocks the
  // thread list itself -- see viewerCoords below, which is simply
  // `undefined` (distance line omitted) until/unless this succeeds. In
  // practice this screen is unreachable without a Profile already
  // (see needsProfile below), so a failure here is a defensive case,
  // not the expected path.
  const ownProfile = useMyProfile();
  const viewerCoords: Coordinates | undefined = ownProfile.isSuccess
    ? { lat: ownProfile.data.lat, lng: ownProfile.data.lng }
    : undefined;

  // Issue #118: incoming reveal requests were invisible outside the
  // Profile tab's buried text link -- the core loop (someone requests
  // you -> you respond) could silently dead-end for days. The Threads
  // screen is the app's home surface, so it carries the banner. Same
  // one /me/activity poll as every other badge surface since issue
  // #395 -- see use-activity.ts.
  const incomingRequestCount = useActivity().data?.summary.pendingRevealRequests ?? 0;

  // One useVote for the whole screen: the optimistic patch walks the
  // cached pages by id, so a single hook instance serves every
  // ThreadCard in the list (issue #241).
  const vote = useVote('thread');

  const needsProfile =
    threadsQuery.isError && threadsQuery.error instanceof ApiError && threadsQuery.error.status === 400;

  return (
    <ScreenContainer scrollUnderDock>
      <YStack flex={1}>
        <XStack pt="$2" pb="$3" alignItems="center" justifyContent="space-between" gap="$2">
          {/* Refresh moved inline next to the radius label (issue #290)
              -- it used to be its own centered row above this one,
              literally the topmost element on this screen, above the
              screen's own identity. Grouped with the radius label
              (rather than the New thread button) since both are
              passive/informational, leaving New thread as the one
              clearly primary action on the right. */}
          <XStack alignItems="center" gap="$2" flexShrink={1} minWidth={0}>
            <WebRefreshButton onRefresh={() => threads.refetch()} refreshing={threads.isRefetching} />
            {/* Issue #151 AC1: was a static "Nearby" label -- doubled as an
              unrelated second name for this screen right below the
              "Threads" tab header/label (AC5's own naming clash), and
              told the user nothing about the actual radius they were
              searching. Now reads the real OwnProfile.radiusM (same
              query this screen already runs for the distance line
              below) via radiusFrameLabel -- resolves AC5 too, since
              "Nearby" as a competing screen name is simply gone.
              Radius PICKING itself is issue #57's separate widget; this
              is display-only. */}
            {/* fontFamily="$body" explicit -- founder, 2026-09-16: "the
              'within ~3 miles of you' font is ugly". Root cause: bare
              `<Text>` (from 'tamagui') has no font-family default of
              its own, unlike Paragraph/Heading (built on SizableText,
              which defaults to "$body") -- it silently falls back to
              the browser/OS system font. Same documented, known gap
              preview/index.tsx's own footer text already fixed this
              same way; this was one of the other real call sites that
              comment flagged as needing its own pass rather than a
              broad, unverified fix. */}
            <RingGlyph size={16} />
            <Text
              color="$colorSecondary"
              fontSize="$meta"
              lineHeight="$meta"
              fontFamily="$body"
              numberOfLines={1}
              flexShrink={1}
            >
              {ownProfile.isSuccess ? radiusFrameLabel(ownProfile.data.radiusM) : 'Nearby'}
            </Text>
          </XStack>
          <PrimaryButton
            size="sm"
            onPress={() => router.push({ pathname: '/threads/new', params: category ? { category } : {} })}
          >
            New thread
          </PrimaryButton>
        </XStack>

        {/* Single-select category filter row (issue #266), "All"
            default. Horizontally scrollable rather than wrapping --
            unlike the composer's picker, this row sits above a
            vertically scrolling list, so a wrap would push the list
            down by a variable amount every time a longer label set is
            shown; a horizontal scroller keeps this row's height fixed. */}
        {/* CONFIRMED root cause of the "half a screen gap" the founder
            reported and re-reported live, 2026-09-18 -- found via a
            real signed-up test account and direct DOM inspection
            (getBoundingClientRect + computed styles), not another
            guess: this horizontal FlatList had no `style` prop at
            all, only `contentContainerStyle`. Without an explicit
            height, react-native-web sized this list's own outer
            clipping container (overflow:hidden) to ~366px -- roughly
            8x the ~44px the chip row actually needs -- while the real
            chip buttons rendered pinned to its TOP, leaving a large
            dead zone below them before the vertical thread list
            began. Confirmed directly: the "mystery" tall block's own
            textContent was literally "AllLocalHobbyPetPeeve...", i.e.
            this exact FlatList's own content. A fixed `style.height`
            (44px button min-height + this row's own 12px
            paddingBottom) constrains the box to what it actually
            renders, the standard fix for a horizontal FlatList with
            no bounded height. */}
        {/* View wrapper + right-edge fade mask -- issue #295: this row
            can clip mid-word ("Recommend…") with no visible cue that
            there's more to scroll to, a hidden-overflow violation
            (components.md rule 7 -- "a hidden overflow needs a visible
            cue"). The fade matches the flat ground
            (surfacePage/$background) exactly, so it reads as "content
            continues" rather than a hard clip. pointerEvents="none" so it never
            blocks a tap on the last visible chip. */}
        {isDesktop ? (
          // Wide screens have room for every chip: wrap instead of clipping.
          <XStack flexWrap="wrap" gap="$2" pb="$3">
            {[{ value: undefined, label: 'All' }, ...THREAD_CATEGORIES].map((item) => (
              <CategoryChip
                key={item.value ?? 'all'}
                label={item.label}
                category={item.value}
                selected={category === item.value}
                onPress={() => setCategory(item.value)}
              />
            ))}
          </XStack>
        ) : (
          <View style={{ position: 'relative' }}>
            <FlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              style={{ height: 52, flexGrow: 0, flexShrink: 0 }}
              data={[{ value: undefined, label: 'All' }, ...THREAD_CATEGORIES]}
              keyExtractor={(c) => c.value ?? 'all'}
              // paddingTop: room for the chips' 44px hit area, which the scroller would clip.
              contentContainerStyle={{ paddingTop: space[1], paddingBottom: space[3], gap: space[2] }}
              renderItem={({ item }) => (
                <CategoryChip
                  label={item.label}
                  category={item.value}
                  selected={category === item.value}
                  onPress={() => setCategory(item.value)}
                />
              )}
            />
            <LinearGradient
              pointerEvents="none"
              colors={['transparent', hexToRgba(colors.surfacePage, 0.95)]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={{ position: 'absolute', right: 0, top: 0, bottom: 12, width: 32 }}
            />
          </View>
        )}

        {incomingRequestCount > 0 && (
          <YStack pb="$3">
            {/* Warning tints, not $card: an incoming request is the one
                thing on this screen that needs action (same semantics as
                NewConnectionRow on the reveal screen), and the banner
                must read as an event, not decoration. */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={
                incomingRequestCount === 1
                  ? '1 pending reveal request'
                  : `${incomingRequestCount} pending reveal requests`
              }
              accessibilityHint="Tap to review"
              onPress={() => router.push({ pathname: '/reveal-requests' })}
            >
              <SolidSurface
                style={{ padding: space[4], borderWidth: borderWidth.hairline, borderColor: colors.actionPrimary }}
              >
                <XStack alignItems="center" gap="$3">
                  <UsersIcon size={iconSize.dock} strokeWidth={iconStroke} color={colors.actionPrimary} />
                  <YStack flex={1} gap="$1">
                    <Text color="$color" fontWeight="$semibold">
                      {incomingRequestCount === 1
                        ? '1 neighbor wants to reveal identities with you'
                        : `${incomingRequestCount} neighbors want to reveal identities with you`}
                    </Text>
                    <Text color="$colorLink" fontSize="$meta" lineHeight="$meta">
                      Tap to review
                    </Text>
                  </YStack>
                </XStack>
              </SolidSurface>
            </Pressable>
          </YStack>
        )}

        <ResourceListView
          resource={threads}
          // threads' own first-load skeleton (four card-shaped
          // placeholders matching the real list's rhythm) -- other
          // states use the shared view's defaults.
          pendingNode={<ThreadsListSkeleton />}
          // The 400 ("no profile yet") takes this screen's own
          // "Create a profile" state instead of the default retry error.
          // Needs-profile is checked before the generic error for the
          // same reason the screen always has: the 400 is a stable,
          // deterministic outcome (a background-refetch failure with
          // cached data still reaches the shared stale notice instead --
          // the absence-of-data guard is the state machine's 'error'
          // branch, lib/list-state.ts).
          errorNode={
            needsProfile ? (
              <EmptyState
                icon={<ProfileIcon size={iconSize.dock} strokeWidth={iconStroke} color={colors.actionPrimary} />}
                title="Create a profile to see what's nearby"
                description="Nearkin uses your profile's location and radius to find threads people are posting near you."
                action={{ label: 'Create profile', onPress: () => router.push('/profile') }}
              />
            ) : undefined
          }
          errorTitle="Couldn't load threads"
          listKey={category ?? 'all'}
          renderItem={({ item, index }) => (
            <RiseIn index={index}>
              <ThreadCard
                thread={item}
                viewerCoords={viewerCoords}
                isOwnThread={item.creatorId === currentUserId}
                router={router}
                voteProps={{
                  score: item.score ?? 0,
                  viewerVote: item.viewerVote,
                  onVote: (direction) => vote.mutate({ id: item.id, direction }),
                  onRemove: () => vote.mutate({ id: item.id }),
                }}
                onPress={() => router.push({ pathname: '/threads/[id]', params: { id: item.id } })}
              />
            </RiseIn>
          )}
          // Moved from a separate sibling branch into
          // ListEmptyComponent -- a plain YStack outside the
          // FlatList has no scrollable ancestor, so the empty state
          // couldn't be pulled to refresh at all (caught in review:
          // "the state users pull hardest on has no pull-to-
          // refresh"). Inside the list, the shared refreshControl
          // now covers it too.
          emptyNode={<EmptyThreadsList />}
          endNote="That's everyone nearby. Quiet, but not empty."
          // Decision 4 (issue #563): a quiet radius leaves most of the
          // screen empty below a short list -- the explainer says why the
          // list is short and what grows it. Zero stays the empty state's
          // job (isSparseList's own doc comment).
          footer={
            isSparseList(threads.items.length, 5) ? (
              <YStack pt="$3">
                <ListExplainer
                  title="Why so few threads?"
                  body="This list is everyone posting within your radius. A quiet neighborhood is the usual reason -- posting one is the fastest fix."
                />
              </YStack>
            ) : undefined
          }
          listProps={{
            // The "half a screen gap" itself turned out to be the
            // category-chip FlatList above (its own unbounded-height
            // bug, see that FlatList's own doc comment) -- NOT this
            // one. This `key` stays anyway as a legitimate, low-risk
            // correctness fix for a real (separate) FlatList/
            // VirtualizedList footgun on react-native-web: stale
            // windowing state (average item height,
            // cellsAroundViewport) can carry over across a `data`
            // swap without a remount. Keyed on `category` only, never
            // on the items' own length -- remounting on every
            // infinite-scroll page load would destroy scroll position.
            style: { flex: 1 },
            ...dockScroll,
            keyExtractor: (thread) => thread.id,
            contentContainerStyle: {
              gap: space[2],
              paddingBottom: space[6] + dockInset,
              ...(threads.items.length === 0 ? { flexGrow: 1 } : null),
            },
          }}
        />
      </YStack>
    </ScreenContainer>
  );
}

// Issue #147 AC3: the first-load state used a bare, centered
// ActivityIndicator with nothing else on screen -- this app's own
// composition rule ("an empty state owns the viewport it is in... a
// small centred block... reads as a page that failed to load") applies
// just as much to a LOADING state as an empty one. Four card-shaped
// Skeletons (same 16px/12px gutter/gap the real FlatList's own
// contentContainerStyle uses, so the swap-in when real cards arrive
// doesn't jump) read as "content is coming," not "something broke."
// Fixed count (not measured against viewport height) -- matches this
// component's own sibling EmptyThreadsList precedent of a simple,
// static render rather than a dynamically-sized one.
function ThreadsListSkeleton() {
  return (
    <YStack gap="$3" pt="$4">
      <Skeleton height={92} />
      <Skeleton height={92} />
      <Skeleton height={92} />
      <Skeleton height={92} />
    </YStack>
  );
}

// Moved out of a separate sibling branch (see ThreadCard's own call
// site above) so it renders inside the FlatList as ListEmptyComponent
// -- matches thread-detail's PostsListStatus pattern for the same
// reason: a plain YStack outside the list had no scrollable ancestor,
// so pull-to-refresh had nothing to attach to on the empty-state path.
function EmptyThreadsList() {
  const router = useRouter();

  return (
    <EmptyState
      ring
      title="No threads near you yet"
      description="Threads people post nearby will show up here. Be the first to start one."
      action={{ label: 'Start a thread', onPress: () => router.push('/threads/new') }}
    />
  );
}

// Acceptance criterion 3 (issue #138): a failed page fetch states
// inline with a retry, and never clears prior pages -- the footer
// logic (and that guarantee) moved to the shared ResourceListFooter
// (components/resource-list-view.tsx) with this screen's migration.
// Kept separate from the first-load error state -- a later-page
// failure must never swap out the whole list.

function ThreadCard({
  thread,
  viewerCoords,
  isOwnThread,
  voteProps,
  router,
  onPress,
}: {
  thread: ThreadListItem;
  viewerCoords: Coordinates | undefined;
  isOwnThread: boolean;
  voteProps: ComponentProps<typeof VoteControl>;
  router: ReturnType<typeof useRouter>;
  onPress: () => void;
}) {
  // Resolved hex values for the inline SVG chevron -- react-native-svg
  // can't read Tamagui token strings ('$colorTertiary'), the same gotcha
  // VoteControl's useVoteColors exists for.
  const theme = useTheme();
  const chevronColor = String(theme.colorTertiary?.val ?? '#8a8175');
  const distanceLabel = viewerCoords
    ? formatDistance(distanceMeters(viewerCoords, { lat: thread.lat, lng: thread.lng }))
    : undefined;

  return (
    // Issue #296, global density pass -- founder, real iPhone
    // screenshot: "everything seems really big (there's very little
    // visible content on the screen)... needs to be a lot more
    // compact." p="$3" (12px, was $4/16px) and the title dropping one
    // step (fontSize="$6"/20px, was $7/24px) below tighten this card's
    // own rhythm; the list's own row gap (this file's FlatList,
    // gap: 8, was 12) tightens the space BETWEEN cards. Left every tap
    // target (avatar, chevron, VoteControl) untouched -- WCAG 2.5.8's
    // 24x24 floor is a floor, not a target for this pass.
    <Card onPress={onPress} p="$3" gap="$1">
      {/* First line, above the title -- who posted this, per the
            whole-app critique's P0 finding that no author identity
            rendered anywhere. pb="$2" gives this row a bit more room
            than the tight $1 gap below it: it's a distinct grouped
            unit (who), not part of the title/body/timestamp rhythm
            (what/when). */}
      {/* The identity row is its OWN pressable (issue #267): the
          card's tap goes to the thread, but the author's profile was
          unreachable from the list at all -- "not apparent to click on
          a profile" -- so reviewing a neighbor before replying
          required opening the thread first. stopPropagation keeps the
          card navigation from firing; the chevron makes the affordance
          visible, not hover-only. */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={isOwnThread ? 'Your profile' : `View ${displayHandle(thread.creatorHandle)}'s profile`}
        {...hit(4)}
        style={{ minHeight: 44, justifyContent: 'center' }}
        onPress={(event) => {
          event.stopPropagation();
          router.push({ pathname: '/users/[userId]', params: { userId: thread.creatorId } });
        }}
      >
        <XStack alignItems="center" gap="$2" pb="$2">
          <PseudonymousAvatar handle={thread.creatorHandle} id={thread.creatorId} size={28} />
          {/* flexShrink + numberOfLines -- handle has no length limit
              anywhere in the stack (domain.NewProfile only rejects an
              empty one), and an XStack row doesn't shrink its children
              by default, so an unusually long handle would push this
              row wider than the card. Matches the body text's own
              numberOfLines={2} truncation two lines down (final
              review, PR #52). */}
          <Text
            color="$colorSecondary"
            fontSize="$meta"
            lineHeight="$meta"
            fontWeight="$semibold"
            textAlign="left"
            flexShrink={1}
            numberOfLines={1}
          >
            {displayHandle(thread.creatorHandle)}
          </Text>
          {/* Founder's own adversarial-lens audit, 2026-09-16: "no visual
            distinction for 'this is you' vs. someone else in a thread
            you started, beyond a text label." Badge, not a colored
            border-left (craft-floor.md's own ban list) or a background
            tint on the whole card (would fight the "one differentiator"
            composition rule if every one of a prolific poster's own
            threads all had a colored card). "primary" variant reuses
            this app's own brand-accent badge pair, the same one a
            status badge (Connected/Declined) already uses elsewhere --
            no new color introduced. */}
          {isOwnThread && (
            <Badge variant="primary" paddingHorizontal="$2" paddingVertical="$0_5">
              You
            </Badge>
          )}
          {/* Visible affordance (issue #267): without it the row reads
              as static text -- the whole reason profiles were
              unreachable from this list. */}
          <ChevronRightIcon size={iconSize.chip} strokeWidth={iconStroke} color={chevronColor} />
        </XStack>
      </Pressable>
      {/* textAlign="left" is explicit, not decorative -- web's
            role="button" (from accessibilityRole above) carries a
            browser default of centered text, and confirmed by
            screenshot that without this override every wrapped line
            here renders centered while the identical Text/YStack pair
            in Preview's (non-Pressable) card renders left-aligned. */}
      {/* numberOfLines={2} -- /impeccable critique, 2026-09-16 (minor
          observation): title had no line guard, unlike body's own
          numberOfLines={2} a few lines down -- MAX_THREAD_TITLE_LEN
          bounds the character count (create-thread/index.tsx) but not
          how many lines that wraps to at a large OS text-scale
          setting or a narrow viewport, so an unusually long title
          could still grow the card unpredictably in this scanning
          list. */}
      <Text
        fontWeight="$semibold"
        color="$color"
        fontSize="$titleS"
        lineHeight="$titleS"
        textAlign="left"
        numberOfLines={2}
      >
        {thread.title}
      </Text>
      {/* Category tag (issue #266) -- one neutral chip treatment with a
          lucide icon per category (decision 8, issue #569): the icon,
          not a hue, is this list's differentiator (components.md's "a
          long list needs one differentiator... surface something that
          varies so the eye has somewhere to land"), and it survives
          color-blindness and both themes without a second signal.
          Sits between title and body so it reads as metadata about the
          thread, not a status on the author (contrast with the "You"
          badge above, which lives in the identity row). */}
      {thread.category && (
        <Metadata
          icon={CATEGORY_ICONS[thread.category]}
          iconColor={chevronColor}
          label={categoryLabel(thread.category)}
        />
      )}
      <Text color="$colorSecondary" numberOfLines={2} textAlign="left">
        <Linkified>{thread.body}</Linkified>
      </Text>
      <AttachmentList attachments={thread.attachments ?? []} preview />
      {/* Distance split out of the meta line into its own small badge --
          2026-09-18 critique finding: proximity is this app's ENTIRE
          differentiator over a generic forum, and it was rendering at
          the exact same tertiary weight as "3d ago", making the one
          thing Nearkin actually offers over Reddit invisible at a
          glance. "primary" variant reuses this app's own brand accent
          (the same pair the "You" badge and status badges already use),
          not a new color -- distance is the ONE piece of metadata worth
          that weight. Timestamp stays plain tertiary text beside it --
          still one visual row, not two, so this doesn't reopen the
          "related items closer" spacing rule the removed comment cited. */}
      <XStack alignItems="center" gap="$3">
        {distanceLabel && <Metadata icon="ring" iconColor={chevronColor} label={distanceLabel} />}
        <Metadata icon={Clock} iconColor={chevronColor} label={relativeTime(thread.createdAt)} />
      </XStack>
      {/* VoteControl sits at the card's tail, after the meta line -- the
          engagement affordance is the LAST thing on the scan, below the
          content it scores (issue #241). Nested Pressables inside the
          card's own press surface are fine on RN: the child wins touch.
          Reply count sits alongside it -- in a thread-based app, "how
          much conversation is here" is the single most important
          triage signal, and it had no presence on the card at all
          before this (issue #288). */}
      <XStack pt="$2" alignItems="center" gap="$3">
        <VoteControl {...voteProps} />
        {isBusyThread(thread.replyCount) ? (
          // Where the conversation is: the one card that leads in a list of
          // similar ones (issue #243). Words, not colour alone.
          <Badge variant="primary">{`${thread.replyCount} replies`}</Badge>
        ) : (
          <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
            {thread.replyCount === 1
              ? '1 reply'
              : thread.replyCount
                ? `${thread.replyCount} replies`
                : 'No replies yet'}
          </Text>
        )}
      </XStack>
    </Card>
  );
}
